import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { DigitaiCreateEngineModelChartVariable, DigitaiEngineModelChartVariable, DigitaiUpdateEngineModelChartVariableById, DigitaiUpdateEngineModelsChartVariables } from '@apps/digitai';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, insertMutation, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/engine-model-chart-variable';
import { GraphQLHeaders, GraphQLService, GridData, parseGqlFields, QueryStatement } from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class EngineModelChartVariableService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiEngineModelChartVariable> | null> = new BehaviorSubject(null);
    engineModelChartVariableSubject$: BehaviorSubject<DigitaiEngineModelChartVariable | null> = new BehaviorSubject(null);
    engineModelsChartVariablesSubject$: BehaviorSubject<DigitaiEngineModelChartVariable[] | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiEngineModelChartVariable> | null>; } = {};
    engineModelChartVariableScoped: { [key: string]: BehaviorSubject<DigitaiEngineModelChartVariable | null>; } = {};
    engineModelsChartVariablesScoped: { [key: string]: BehaviorSubject<DigitaiEngineModelChartVariable[] | null>; } = {};

    constructor(
        private readonly graphqlService: GraphQLService,
    ) {}

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiEngineModelChartVariable>>
    {
        return this.paginationSubject$.asObservable();
    }

    get engineModelChartVariable$(): Observable<DigitaiEngineModelChartVariable>
    {
        return this.engineModelChartVariableSubject$.asObservable();
    }

    get engineModelsChartVariables$(): Observable<DigitaiEngineModelChartVariable[]>
    {
        return this.engineModelsChartVariablesSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiEngineModelChartVariable>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiEngineModelChartVariable>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();

        this.paginationScoped[scope] = new BehaviorSubject(null);
        return this.paginationScoped[scope].asObservable();
    }

    setScopeEngineModelChartVariable(scope: string, object: DigitaiEngineModelChartVariable): void
    {
        if (this.engineModelChartVariableScoped[scope])
        {
            this.engineModelChartVariableScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.engineModelChartVariableScoped[scope] = new BehaviorSubject(object);
    }

    getScopeEngineModelChartVariable(scope: string): Observable<DigitaiEngineModelChartVariable>
    {
        if (this.engineModelChartVariableScoped[scope]) return this.engineModelChartVariableScoped[scope].asObservable();

        this.engineModelChartVariableScoped[scope] = new BehaviorSubject(null);
        return this.engineModelChartVariableScoped[scope].asObservable();
    }

    setScopeEngineModelsChartVariables(scope: string, objects: DigitaiEngineModelChartVariable[]): void
    {
        if (this.engineModelsChartVariablesScoped[scope])
        {
            this.engineModelsChartVariablesScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.engineModelsChartVariablesScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeEngineModelsChartVariables(scope: string): Observable<DigitaiEngineModelChartVariable[]>
    {
        if (this.engineModelsChartVariablesScoped[scope]) return this.engineModelsChartVariablesScoped[scope].asObservable();

        this.engineModelsChartVariablesScoped[scope] = new BehaviorSubject(null);
        return this.engineModelsChartVariablesScoped[scope].asObservable();
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiEngineModelChartVariable>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiEngineModelChartVariable>; }>({
                query    : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            engineModelId = null,
            chartVariableId = null,
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            engineModelId?: string;
            chartVariableId?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiEngineModelChartVariable;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiEngineModelChartVariable;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    engineModelId,
                    chartVariableId,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeEngineModelChartVariable(scope, data.object) : this.engineModelChartVariableSubject$.next(data.object)),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiEngineModelChartVariable;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiEngineModelChartVariable;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeEngineModelChartVariable(scope, data.object) : this.engineModelChartVariableSubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiEngineModelChartVariable[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiEngineModelChartVariable[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeEngineModelsChartVariables(scope, data.objects) : this.engineModelsChartVariablesSubject$.next(data.objects)),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateEngineModelChartVariable;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    insert<T>(
        {
            graphqlStatement = insertMutation,
            objects = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            objects?: DigitaiCreateEngineModelChartVariable[];
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: objects,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateEngineModelChartVariableById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateEngineModelsChartVariables;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            engineModelId = null,
            chartVariableId = null,
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            engineModelId?: string;
            chartVariableId?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    engineModelId,
                    chartVariableId,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }
}
